import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import Select from "../../../../components/input/Select";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import {
	ReportLevel,
	AdminReportsResponse,
	useGetSchoolYearsForReportsQuery,
} from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import { DraftUsageRow, useLazyDraftUsageOverTimeQuery } from "./adminUsageReportApiSlice";
import { DraftUsageTable } from "./DraftUsageTable";
import { DraftUsageChart } from "./DraftUsageChart";

export default function DraftUsageOverTime() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<DraftUsageRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [schoolYearOptions, setSchoolYearOptions] = useState<SimpleOption[]>([]);
	const [yearFilter, setYearFilter] = useState<string>();

	const getSchoolYearsResult = useGetSchoolYearsForReportsQuery(undefined);
	const schoolYears = getSchoolYearsResult.data;

	const [callDraftUsageOverTime] = useLazyDraftUsageOverTimeQuery();

	useEffect(() => {
		if (schoolYears) {
			const options = schoolYears.map(
				(d) => ({ value: d.value, label: d.text }) as SimpleOption
			);

			setSchoolYearOptions(options);
			setYearFilter(options[0].value as string);
		}
	}, [schoolYears]);

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		if (yearFilter) {
			callDraftUsageOverTime(
				{
					districtId: districtFilter === 0 ? undefined : districtFilter,
					schoolId: schoolFilter === 0 ? undefined : schoolFilter,
					schoolYear: Number(yearFilter),
				},
				true //cache the results
			)
				.unwrap()
				.then((payload) => {
					setData(payload.response);
					setLastUpdated(payload.lastUpdateDate);
					setReportLevel(getReportLevel(districtFilter, schoolFilter));
				})
				.catch((error) => {
					console.log(error.data);
					navigate("/adminReports");
				});
		}
	}, [districtFilter, schoolFilter, yearFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
			yearFilter,
		});
	}, [districtFilter, schoolFilter, yearFilter]);

	return (
		<section>
			<h1>
				{reportLevel} Draft Usage Over Time by {reportSubLevelName(reportLevel)}
			</h1>
			<p className="mt-4 print:hidden">{`The Draft Usage Over Time Report shows the number of draft submissions by month. Use the filters below ${reportLevel !== ReportLevel.School ? "or select the name in the table" : ""} to view data for that selection.  Click the three dots to print or download.`}</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end gap-x-3">
							<div className="flex flex-wrap gap-3 items-end print:hidden">
								<Select
									value={yearFilter}
									setValue={(v) => setYearFilter(v as string)}
									label="School Year"
									simpleListBoxId="yearFilter"
									options={schoolYearOptions}
									fieldClasses="w-full md:flex-1"
									simpleListBoxClasses="w-52"
								/>
								<DistrictSchoolFilters
									districtOptions={districtOptions}
									schoolOptions={schoolOptions}
									districtFilter={districtFilter}
									schoolFilter={schoolFilter}
									setDistrictFilter={setDistrictFilter}
									setSchoolFilter={setSchoolFilter}
								/>
							</div>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={data.rows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="draft-usage"
								downloadExcelLink={`${aspUrl}/AdminReports/DraftUsageExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/DraftUsageCsv?${queryString}`}
							/>
						</div>

						<DraftUsageTable
							className="mt-4"
							level={reportLevel}
							data={data}
							hideRows={hideRows}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
						/>
						<div className="print:hidden">
							<DraftUsageChart
								level={reportLevel}
								totals={data.totals}
								hasData={data.rows.length > 0}
							/>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
